import { ItemArt } from "../art/ItemArt";
import { itemById, type ItemKind } from "../content/items";
import type { Ninja } from "../content/ninjas";
import { loadoutFor, type Loadouts, type OwnedItems } from "../game/items";
import "./SlotRow.css";

interface Props {
  ninja: Ninja;
  /** The child's loadouts; left out to show the empty layout. */
  loadouts?: Loadouts | undefined;
  /** What the child owns; a slot naming something unowned shows empty. */
  items?: OwnedItems | undefined;
  /** Tapping a slot opens the picker. Read-only without it. */
  onPick?: ((kind: ItemKind, index: number) => void) | undefined;
  /** Greyed: the ninja isn't recruited yet. */
  locked?: boolean | undefined;
  className?: string | undefined;
}

const KIND_LABEL: Record<ItemKind, string> = { active: "Use", passive: "Wear" };

/**
 * A ninja's slots: red circles for active items, blue squares for passive,
 * each showing the item fitted. Tapping one (when allowed) picks an item.
 */
export function SlotRow({ ninja, loadouts, items, onPick, locked = false, className }: Props) {
  const loadout = loadoutFor(loadouts ?? {}, ninja);
  const slots = (kind: ItemKind) =>
    loadout[kind].map((id, i) => {
      const item = id ? itemById(id) : undefined;
      const level = item && items ? (items[item.id]?.level ?? 0) : 0;
      const filled = item && item.kind === kind && level > 0 ? item : undefined;
      const label = `${KIND_LABEL[kind]} slot ${i + 1}` + (filled ? `: ${filled.name}` : ": empty");
      const inner = filled ? (
        <ItemArt id={filled.id} className="slot-art" />
      ) : (
        <span className="slot-empty" aria-hidden="true">
          {kind === "active" ? "+" : "+"}
        </span>
      );
      return onPick ? (
        <button
          key={`${kind}-${i}`}
          type="button"
          className={`slot ${kind}` + (filled ? " filled" : "")}
          aria-label={label + ". Change"}
          onClick={() => onPick(kind, i)}
        >
          {inner}
        </button>
      ) : (
        <span
          key={`${kind}-${i}`}
          className={`slot ${kind}` + (filled ? " filled" : "")}
          role="img"
          aria-label={label}
        >
          {inner}
        </span>
      );
    });
  return (
    <div className={["slots", locked ? "locked" : "", className].filter(Boolean).join(" ")}>
      {slots("active")}
      {slots("passive")}
    </div>
  );
}
